import { Button, Group, Loader, Stack, Text } from '@mantine/core'
import { useInterval, useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconDeviceMobile, IconDeviceTablet, IconRefresh } from '@tabler/icons-react'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDateTime } from '@tnn/core'
import type {
  ClockScreenState,
  LiveEvent,
  PairedClockScreen,
  PairedScreen,
  ScreenPairInput,
  ScreenPairing,
  ScreenState,
  Task,
  TaskResultInput,
} from '@tnn/domain'
import { currentLocale } from '@tnn/i18n'
import { ResponsiveDialog, SectionCard } from '@tnn/ui'
import type { TFunction } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE, api } from '../api/client'
import { siteLink } from '../app/links'
import { boardsKey } from '../boards'
import { chatRoomsKey } from '../chat'
import { QrCode } from '../scan/QrCode'
import { tasksKey } from '../tasks/api'
import classes from './screen.module.css'

/**
 * The portable screen (จอพกพา, screen.ts in @tnn/domain) as one's own apps see it: whether one has one (`useMyScreen`),
 * pairing one (`PairScreenDialog`, `useScreenGate`), and the events that tell open pages what changed
 * (`useLiveEvents`). The screen's own page uses `screenApi`'s device half.
 */

export const screenKey = ['me', 'screen'] as const

export const screenApi = {
  mine: () => api.get<{ item: PairedScreen | null }>('/me/screen').then((r) => r.item),
  pairing: () => api.post<ScreenPairing>('/me/screen/pairing'),
  unpair: () => api.delete<{ item: null }>('/me/screen'),
  // The device: signed in by the cookie pairing gave it, as a person's screen (`owner`) or a branch's clock screen (`clock`)
  pair: (input: ScreenPairInput) => api.post<{ owner?: { name: string }; clock?: PairedClockScreen }>('/public/screen/pair', input),
  state: () => api.get<ScreenState>('/screen'),
  upload: (file: File) => api.upload(file, '/screen/files'),
  done: (id: string, input: TaskResultInput) =>
    api.post<{ item: Task }>(`/screen/tasks/${encodeURIComponent(id)}/done`, input).then((r) => r.item),
  leave: () => api.delete<{ ok: boolean }>('/screen'),
  /** A clock screen's QR of the moment (its own cookie) */
  clock: () => api.get<ClockScreenState>('/screen/clock'),
}

/** One's screen, or null; told again by `useLiveEvents` when it is paired, opened, closed or unpaired */
export function useMyScreen(enabled = true) {
  return useQuery({ queryKey: screenKey, queryFn: screenApi.mine, enabled, staleTime: 5 * 60_000 })
}

/** The QR's link, on the screen's own site (apps/screen): a phone's camera opens it, the screen's scanner reads the token from it */
export const pairingLink = (token: string) => siteLink('screen', `#${token}`)

/** The pairing token in what was scanned (the link, or the token alone), or null */
export function pairingToken(text: string): string | null {
  const token = text.includes('#') ? text.slice(text.lastIndexOf('#') + 1) : text
  return /^[\w-]{20,100}$/.test(token) ? token : null
}

/** What the device is, for its person to tell it by: "iPad", "Android tablet" */
export function deviceName(t: TFunction) {
  const ua = navigator.userAgent
  const touchMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
  if (/iPad/.test(ua) || touchMac) return 'iPad'
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/Android/.test(ua)) return t(/Mobile/.test(ua) ? 'screen.device.androidPhone' : 'screen.device.androidTablet')
  return t('screen.device.computer')
}

/**
 * Listens to server-sent `LiveEvent`s at `path` while the page is open, opening again after it is lost (the browser
 * gives up on an answer that is not a stream — signed out, a PIN to type, unpaired — so this waits longer each time)
 */
export function listenLive(path: string, onEvent: (event: LiveEvent) => void) {
  let source: EventSource | null = null
  let tries = 0
  let timer: number | undefined
  const open = () => {
    source = new EventSource(`${API_BASE}${path}`, { withCredentials: true })
    source.onmessage = (message) => {
      tries = 0
      try {
        onEvent(JSON.parse(message.data) as LiveEvent)
      } catch {
        // Not an event of ours
      }
    }
    source.onerror = () => {
      if (source?.readyState !== EventSource.CLOSED) return
      source.close()
      timer = window.setTimeout(open, Math.min(60_000, 3000 * 2 ** tries++))
    }
  }
  open()
  return () => {
    window.clearTimeout(timer)
    source?.close()
  }
}

/**
 * What a person's apps fetch again when told: their tasks (and a field waiting on one), their screen, their chat rooms,
 * the boards they see
 */
function refresh(client: QueryClient, event: LiveEvent) {
  if (event.type === 'task' || event.type === 'hello') client.invalidateQueries({ queryKey: tasksKey })
  if (event.type === 'screen' || event.type === 'hello') client.invalidateQueries({ queryKey: screenKey })
  if (event.type === 'chat' || event.type === 'hello') client.invalidateQueries({ queryKey: chatRoomsKey })
  if (event.type === 'board' || event.type === 'hello') client.invalidateQueries({ queryKey: boardsKey })
}

/** Signed in, in the apps' shell: one stream a page (`/api/me/events`) */
export function useLiveEvents() {
  const client = useQueryClient()
  useEffect(() => listenLive('/me/events', (event) => refresh(client, event)), [client])
}

/** Seconds left until `at` (ISO), ticking */
export function useSecondsLeft(at: string | undefined) {
  const [now, setNow] = useState(() => Date.now())
  const tick = useInterval(() => setNow(Date.now()), 1000)
  // biome-ignore lint/correctness/useExhaustiveDependencies: ticks while there is something to count down to
  useEffect(() => {
    if (!at) return
    tick.start()
    return tick.stop
  }, [at])
  return at ? Math.max(0, Math.round((new Date(at).getTime() - now) / 1000)) : 0
}

/**
 * A pairing shown (`useSecondsLeft(shown.expiresAt)` is `left`): its QR to scan with the device, the code under it to
 * type, and how long it still works — faded once spent. A clock screen's pairing (HR) shows the same
 */
export function PairingCode({ shown, left, label, failed }: { shown?: ScreenPairing; left: number; label: string; failed?: boolean }) {
  const { t } = useTranslation()
  const spent = !!shown && left === 0
  if (failed) return <Text c="var(--tnn-error)">{t('screen.failed')}</Text>
  if (!shown) return <Loader size="sm" />
  return (
    <>
      <QrCode value={pairingLink(shown.token)} size={216} label={label} className={spent ? classes.spent : undefined} />
      <div className={classes.code} data-spent={spent || undefined}>
        {shown.code.slice(0, 3)} {shown.code.slice(3)}
      </div>
      <Text size="sm" c="dimmed" ta="center">
        {spent ? t('screen.spent') : t('screen.expiresIn', { seconds: left })}
      </Text>
    </>
  )
}

/**
 * Pairing a portable screen: a QR to scan with the screen's app (or a phone's camera) and the code under it to type,
 * good once for a couple of minutes. Closes by itself once a screen is paired (`onPaired`). On a phone or tablet it can
 * make this very device one's screen instead.
 */
export function PairScreenDialog({ opened, onClose, onPaired }: { opened: boolean; onClose: () => void; onPaired?: () => void }) {
  const { t } = useTranslation()
  const client = useQueryClient()
  const touch = useMediaQuery('(pointer: coarse)')
  const mine = useMyScreen(opened)
  const pairing = useMutation({ mutationFn: screenApi.pairing })
  const left = useSecondsLeft(pairing.data?.expiresAt)
  const openedAt = useRef(0)
  const paired = useRef(onPaired)
  paired.current = onPaired
  const [here, setHere] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new pairing each time it opens
  useEffect(() => {
    if (!opened) return
    openedAt.current = Date.now()
    pairing.mutate()
  }, [opened])

  // A screen paired since it opened (heard at once, or asked again every few seconds meanwhile)
  const screen = mine.data
  useEffect(() => {
    if (!opened || !screen || new Date(screen.pairedAt).getTime() < openedAt.current - 2000) return
    notifications.show({ message: t('screen.pairedWith', { name: screen.name }) })
    onClose()
    paired.current?.()
  }, [opened, screen, onClose, t])
  useEffect(() => {
    if (!opened) return
    const again = window.setInterval(() => client.invalidateQueries({ queryKey: screenKey }), 4000)
    return () => window.clearInterval(again)
  }, [opened, client])

  /** This phone or tablet becomes the screen: it opens the QR's link, and the screen's site pairs itself with the token */
  const pairThisDevice = () => {
    if (!pairing.data) return
    setHere(true)
    window.location.assign(pairingLink(pairing.data.token))
  }

  const shown = pairing.data
  const spent = !!shown && left === 0

  return (
    <ResponsiveDialog
      opened={opened}
      onClose={onClose}
      title={t('screen.pairTitle')}
      size="sm"
      footer={
        <>
          {spent && (
            <Button variant="light" leftSection={<IconRefresh size={18} />} onClick={() => pairing.mutate()}>
              {t('screen.newCode')}
            </Button>
          )}
          <Button variant="default" onClick={onClose}>
            {t('actions.close')}
          </Button>
        </>
      }
    >
      <Stack gap="md" align="center">
        <Text size="sm" ta="center">
          {t('screen.pairHint', { url: new URL(siteLink('screen')).host })}
        </Text>
        <PairingCode shown={shown} left={left} label={t('screen.scanThis')} failed={pairing.isError} />
        {screen && (
          <Text size="xs" c="dimmed" ta="center">
            {t('screen.replaces', { name: screen.name })}
          </Text>
        )}
        {touch && shown && !spent && (
          <Group justify="center">
            <Button variant="subtle" leftSection={<IconDeviceMobile size={18} />} loading={here} onClick={pairThisDevice}>
              {t('screen.useThisDevice')}
            </Button>
          </Group>
        )}
      </Stack>
    </ResponsiveDialog>
  )
}

/**
 * Doing something on one's screen: at once when one has one, otherwise once one is paired (the dialog, which the field
 * renders as `dialog`). `enabled`: someone is signed in (a public page has no screen to ask about)
 */
export function useScreenGate(enabled = true) {
  const screen = useMyScreen(enabled).data ?? null
  const [waiting, setWaiting] = useState<(() => void) | null>(null)
  const run = (then: () => void) => (screen ? then() : setWaiting(() => then))
  const dialog = (
    <PairScreenDialog
      opened={!!waiting}
      onClose={() => setWaiting(null)}
      onPaired={() => {
        const then = waiting
        setWaiting(null)
        then?.()
      }}
    />
  )
  return { screen, run, dialog }
}

/** บัญชีของฉัน: one's portable screen — which device, open or not, since when — to pair, pair anew or unpair */
export function MyScreenCard() {
  const { t, i18n } = useTranslation()
  const client = useQueryClient()
  const screen = useMyScreen()
  const [pairing, setPairing] = useState(false)
  const unpair = useMutation({
    mutationFn: screenApi.unpair,
    onSuccess: () => client.setQueryData(screenKey, null),
  })
  const item = screen.data
  return (
    <SectionCard title={t('screen.name')}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('screen.hint')}
        </Text>
        {item ? (
          <Group gap="sm" wrap="nowrap">
            <IconDeviceTablet size={22} />
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text size="sm" fw={500}>
                {item.name}
              </Text>
              <Text size="xs" c="dimmed">
                {[
                  t(item.online ? 'screen.online' : 'screen.offline'),
                  t('screen.pairedAt', {
                    at: formatDateTime(item.pairedAt, { locale: currentLocale(i18n), style: 'short' }),
                  }),
                ].join(' · ')}
              </Text>
            </Stack>
          </Group>
        ) : (
          screen.isSuccess && (
            <Text size="sm" fw={500}>
              {t('screen.none')}
            </Text>
          )
        )}
        <Group gap="sm">
          <Button variant="light" leftSection={<IconDeviceTablet size={18} />} onClick={() => setPairing(true)}>
            {t('screen.pair')}
          </Button>
          {item && (
            <Button variant="subtle" color="danger" loading={unpair.isPending} onClick={() => unpair.mutate()}>
              {t('screen.unpair')}
            </Button>
          )}
        </Group>
      </Stack>
      <PairScreenDialog opened={pairing} onClose={() => setPairing(false)} />
    </SectionCard>
  )
}
